import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import {
  type Trattativa,
  useCreaTrattativa,
  useOpzioniTrattative,
  useSalvaTrattativa,
} from '../api/trattative.js';
import { Modale } from '../componenti/Modale.js';
import { giornoItaliano } from '../giorni.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * Il modulo di una trattativa: gli stessi campi per crearla e per modificarla.
 *
 * La probabilità è un cursore, come nel v2 — è una stima, e un cursore lo
 * dice meglio di una casella dove si scrive «55». Accanto c'è il valore
 * pesato, che si aggiorna mentre lo si muove: è quel numero, non il budget,
 * quello che finisce nelle somme.
 */
export function ModaleTrattativa({
  trattativa,
  onChiudi,
  onCreata,
}: {
  /** Nulla per una trattativa nuova. */
  trattativa: Trattativa | null;
  onChiudi: () => void;
  onCreata?: (id: number) => void;
}) {
  const crea = useCreaTrattativa();
  const salva = useSalvaTrattativa(trattativa?.id ?? 0);
  const opzioni = useOpzioniTrattative();

  const [cerca, setCerca] = useState('');
  const [cliente, setCliente] = useState<{ id: number; name: string } | null>(
    trattativa ? { id: trattativa.customerId, name: trattativa.customer ?? '' } : null,
  );
  const [campi, setCampi] = useState({
    subject: trattativa?.subject ?? '',
    status: trattativa?.status ?? 'in_progress',
    // uno zero è un budget: «vero o falso» lo faceva diventare «nessun budget»
    budgetAmount:
      trattativa?.budgetAmount !== null && trattativa?.budgetAmount !== undefined
        ? String(trattativa.budgetAmount)
        : '',
    expectedClosingDate: giornoItaliano(trattativa?.expectedClosingDate),
    probabilityClosingDeal: String(trattativa?.probabilityClosingDeal ?? 50),
    salesmanId: trattativa?.salesmanId ? String(trattativa.salesmanId) : '',
    agentId: trattativa?.agentId ? String(trattativa.agentId) : '',
    customerNeeds: trattativa?.customerNeeds ?? '',
    notes: trattativa?.notes ?? '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  /**
   * Il cliente di una trattativa può essere un lead o un prospect, non solo
   * un cliente: è il senso della parola trattativa — si sta ancora trattando.
   */
  const anagrafiche = useQuery({
    queryKey: ['anagrafiche-per-trattativa', cerca],
    queryFn: async () => {
      const tipi = ['customer', 'lead', 'prospect'] as const;
      const risposte = await Promise.all(
        tipi.map((tipo) =>
          chiama<{ rows: { id: number; name: string; bcCodeSpa: string | null }[] }>(
            `/api/identities?type=${tipo}&size=6&q=${encodeURIComponent(cerca)}`,
          ).then((r) => r.rows.map((x) => ({ ...x, tipo }))),
        ),
      );
      return risposte.flat();
    },
    enabled: cerca.trim().length >= 2,
  });

  const ETICHETTA_TIPO: Record<string, string> = {
    customer: 'cliente',
    lead: 'lead',
    prospect: 'prospect',
  };

  const inCorso = crea.isPending || salva.isPending;
  const errore =
    crea.error instanceof ErroreApi
      ? crea.error.message
      : salva.error instanceof ErroreApi
        ? salva.error.message
        : null;

  const budget = Number(campi.budgetAmount || 0);
  const probabilita = Number(campi.probabilityClosingDeal || 0);
  const pesato = budget > 0 ? (budget * probabilita) / 100 : null;

  const conferma = () => {
    const corpo = {
      customerId: cliente!.id,
      subject: campi.subject.trim(),
      status: campi.status,
      budgetAmount: campi.budgetAmount === '' ? null : Number(campi.budgetAmount),
      expectedClosingDate: campi.expectedClosingDate || null,
      probabilityClosingDeal: probabilita,
      salesmanId: campi.salesmanId ? Number(campi.salesmanId) : null,
      agentId: campi.agentId ? Number(campi.agentId) : null,
      customerNeeds: campi.customerNeeds.trim() || null,
      notes: campi.notes.trim() || null,
    };

    if (trattativa) {
      salva.mutate(corpo, { onSuccess: onChiudi });
    } else {
      crea.mutate(corpo, { onSuccess: ({ id }) => onCreata?.(id) });
    }
  };

  const pronto = cliente !== null && campi.subject.trim() !== '';

  return (
    <Modale
      titolo={trattativa ? t('Modifica la trattativa') : t('Nuova trattativa')}
      occhiello={trattativa?.customer ?? undefined}
      larga
      inCorso={inCorso}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={inCorso}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!pronto || inCorso}
          >
            {inCorso ? 'Salvo…' : trattativa ? 'Salva' : t('Crea la trattativa')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label htmlFor="tr-cliente">
          {t('Cliente')}<span className="req">*</span>
          <span className="dim" style={{ fontWeight: 400 }}>
            {' '}
            {t('— anche un lead o un prospect: si sta ancora trattando')}
          </span>
        </label>
        {cliente ? (
          <div className="scelto">
            <strong>{cliente.name}</strong>
            <button className="btn btn-ghost" onClick={() => setCliente(null)}>
              {t('Cambia')}
            </button>
          </div>
        ) : (
          <>
            <input
              id="tr-cliente"
              className="input"
              placeholder={t('Scrivi almeno due lettere')}
              value={cerca}
              autoFocus
              onChange={(e) => setCerca(e.target.value)}
            />
            {(anagrafiche.data ?? []).length > 0 && (
              <ul className="risultati">
                {anagrafiche.data!.map((a) => (
                  <li key={`${a.tipo}-${a.id}`}>
                    <button
                      className="risultato"
                      onClick={() => {
                        setCliente({ id: a.id, name: a.name });
                        setCerca('');
                      }}
                    >
                      <span>{a.name}</span>
                      <span className="badge">{ETICHETTA_TIPO[a.tipo]}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="tr-oggetto">
            {t('Oggetto')}<span className="req">*</span>
          </label>
          <input
            id="tr-oggetto"
            className="input"
            placeholder={t('Proposta listino sughi')}
            value={campi.subject}
            onChange={(e) => cambia('subject', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tr-stato">{t('Stato')}</label>
          <select
            id="tr-stato"
            className="select"
            value={campi.status}
            onChange={(e) => cambia('status', e.target.value)}
          >
            {(opzioni.data?.negotiationStates ?? []).map((s) => (
              <option key={s.value} value={s.value}>
                {s.it}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="tr-chiusura">{t('Chiusura prevista')}</label>
          <input
            id="tr-chiusura"
            className="input"
            type="date"
            value={campi.expectedClosingDate}
            onChange={(e) => cambia('expectedClosingDate', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tr-budget">{t('Budget')}</label>
          <input
            id="tr-budget"
            className="input num"
            type="number"
            step="100"
            min="0"
            value={campi.budgetAmount}
            onChange={(e) => cambia('budgetAmount', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tr-venditore">{t('Venditore')}</label>
          <select
            id="tr-venditore"
            className="select"
            value={campi.salesmanId}
            onChange={(e) => cambia('salesmanId', e.target.value)}
          >
            <option value="">{t('Nessuno')}</option>
            {(opzioni.data?.salesmen ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="tr-agente">{t('Agente')}</label>
          <select
            id="tr-agente"
            className="select"
            value={campi.agentId}
            onChange={(e) => cambia('agentId', e.target.value)}
          >
            <option value="">{t('Nessuno')}</option>
            {(opzioni.data?.agents ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* la probabilità è una stima: un cursore lo dice meglio di una casella,
          e accanto si vede subito cosa fa al valore atteso */}
      <div className="field span-2" style={{ margin: '14px 0' }}>
        <label htmlFor="tr-prob">
          {t('Probabilità di chiusura:')} <strong>{probabilita}%</strong>
          {pesato !== null && (
            <span className="dim" style={{ fontWeight: 400 }}>
              {' '}
              {t('— valore atteso')}{' '}
              {pesato.toLocaleString(locale(), {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
              })}
            </span>
          )}
        </label>
        <input
          id="tr-prob"
          type="range"
          min={0}
          max={100}
          step={5}
          value={campi.probabilityClosingDeal}
          onChange={(e) => cambia('probabilityClosingDeal', e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="tr-esigenze">{t('Esigenze del cliente')}</label>
          <textarea
            id="tr-esigenze"
            className="input"
            rows={3}
            value={campi.customerNeeds}
            onChange={(e) => cambia('customerNeeds', e.target.value)}
          />
        </div>
        <div className="field span-2">
          <label htmlFor="tr-note">{t('Note')}</label>
          <textarea
            id="tr-note"
            className="input"
            rows={3}
            value={campi.notes}
            onChange={(e) => cambia('notes', e.target.value)}
          />
        </div>
      </div>
    </Modale>
  );
}
